import { onMounted, onUnmounted, ref } from 'vue'

// Banner root. Production uses our custom Cookiebot template whose root is
// `#cookiebanner` (see kova-cookie-banner/prod_banner/banner.html — it owns this id,
// so it's stable). Dev/preview shows Cookiebot's default banner `#CybotCookiebotDialog`.
// We support both so the logic works in both environments.
const BANNER_ROOT_IDS = ['cookiebanner', 'CybotCookiebotDialog']
// The visible bottom bar inside the custom banner — used for height (the root also
// contains the hidden "Manage" modal, so measure the bar, not the root).
const BANNER_BAR_SELECTOR = '.cb-banner'
const BANNER_HEIGHT_VAR = '--cookie-banner-height'

/**
 * Reactive visibility of the Cookiebot consent banner, derived from the banner's
 * real DOM state — NOT from `CookiebotOnAccept`, which fires ~1s after the banner
 * has already started closing.
 *
 * A `MutationObserver` on `#CybotCookiebotDialog` flips `bannerVisible` to `false`
 * the instant Cookiebot sets inline `opacity: 0` (the start of its close, ≈ at the
 * click), so a consumer can drop its UI in the same tick — no fade, no delay.
 *
 * `bannerVisible` defaults to `false`, so the consumer can never be shown without a
 * banner actually on screen.
 *
 * Also publishes the banner height to `--cookie-banner-height` on <html> so a notice
 * can sit flush above the bottom-sheet banner.
 */
export function useCookieBannerVisible() {
	const bannerVisible = ref(false)
	// Two observers, each for one job: MutationObserver catches the instant close
	// (opacity:0 / display:none) for synced hiding; ResizeObserver tracks the banner
	// height for positioning (fires with the real size even when the banner lays out
	// a tick after it's shown — a MutationObserver misses that, it's not an attr change).
	let mutationObserver = null
	let resizeObserver = null

	function findBannerRoot() {
		for (const id of BANNER_ROOT_IDS) {
			const el = document.getElementById(id)
			if (el) return el
		}
		return null
	}

	function isBannerVisible(root) {
		// Custom banner: hidden via `display: none` on the root (it starts that way).
		// Default banner: Cookiebot sets inline `opacity: 0` first, then `display: none`.
		// Both close paths are caught by checking display + inline opacity.
		return getComputedStyle(root).display !== 'none' && root.style.opacity !== '0'
	}

	function publishHeight(root) {
		// Measure the visible bar, not the root (the root also holds the hidden
		// "Manage" modal). Falls back to the root for the default banner.
		const bar = root.querySelector(BANNER_BAR_SELECTOR) || root
		const height = Math.round(bar.getBoundingClientRect().height)
		document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, `${height}px`)
	}

	function stopObserving() {
		mutationObserver?.disconnect()
		mutationObserver = null
		resizeObserver?.disconnect()
		resizeObserver = null
	}

	function syncFromBanner() {
		const root = findBannerRoot()
		if (!root || !isBannerVisible(root)) {
			bannerVisible.value = false
			stopObserving()
			return
		}

		bannerVisible.value = true
		publishHeight(root)

		if (mutationObserver) return

		// Height: fires immediately with the current size and on every resize
		// (covers late layout after show, "Manage" expansion, orientation changes).
		const bar = root.querySelector(BANNER_BAR_SELECTOR) || root
		resizeObserver = new ResizeObserver(() => {
			if (isBannerVisible(root)) publishHeight(root)
		})
		resizeObserver.observe(bar)

		// Visibility: drop the notice the instant the banner starts closing.
		mutationObserver = new MutationObserver(() => {
			if (isBannerVisible(root)) {
				publishHeight(root)
				return
			}
			bannerVisible.value = false
			stopObserving()
		})
		mutationObserver.observe(root, { attributes: true, attributeFilter: ['style', 'class'] })
	}

	onMounted(() => {
		syncFromBanner()
		// Catch the banner if it appears after mount (entered the funnel without it).
		window.addEventListener('CookiebotOnDialogDisplay', syncFromBanner)
	})

	onUnmounted(() => {
		stopObserving()
		window.removeEventListener('CookiebotOnDialogDisplay', syncFromBanner)
	})

	return { bannerVisible }
}
